import {
  POI_CATEGORIES,
  POI_COLORS,
  translate,
  type PoiCategory,
} from "@festipal/shared";
import {
  LngLatBounds,
  Map as MapLibreMap,
  Marker,
  NavigationControl,
  type MapMouseEvent,
} from "maplibre-gl";
import { useCallback, useEffect, useRef, useState } from "react";
import "maplibre-gl/dist/maplibre-gl.css";

import { festivals } from "./api";
import { loadBasemap } from "./basemap";

/**
 * Édition des lieux, directement sur la carte.
 *
 * Poser un point d'intérêt revient à cliquer là où il se trouve : saisir des
 * coordonnées à la main serait à la fois pénible et propice aux inversions de
 * latitude et de longitude, l'erreur classique qui envoie un poste de secours en
 * mer.
 */

type BundlePoiShape = {
  id: string;
  category: PoiCategory;
  latitude: number;
  longitude: number;
  verified: boolean;
  name: Record<string, string>;
};

type BundleShape = {
  festival: { defaultLocale: string };
  pois: BundlePoiShape[];
};

export function PoiEditor({ slug }: { slug: string }) {
  const container = useRef<HTMLDivElement | null>(null);
  const map = useRef<MapLibreMap | null>(null);
  const markers = useRef<Marker[]>([]);

  const [pois, setPois] = useState<BundlePoiShape[]>([]);
  const [category, setCategory] = useState<PoiCategory>("toilets");
  const [name, setName] = useState("");
  const [pending, setPending] = useState<{ lat: number; lng: number } | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [mapReady, setMapReady] = useState(false);

  const reload = useCallback(async () => {
    const bundle = (await festivals.bundle(slug)) as BundleShape;
    setPois(bundle.pois);
    return bundle;
  }, [slug]);

  // Création de la carte, une seule fois.
  useEffect(() => {
    let instance: MapLibreMap | null = null;
    let abandoned = false;

    void loadBasemap().then((basemap) => {
      // Le composant peut avoir été démonté pendant la lecture de l'archive.
      if (abandoned || !container.current) return;

      instance = new MapLibreMap({
        container: container.current,
        style: basemap.style,
        center: basemap.center,
        zoom: basemap.zoom,
      });

      instance.addControl(new NavigationControl(), "top-right");
      instance.on("click", (event: MapMouseEvent) =>
        setPending({ lat: event.lngLat.lat, lng: event.lngLat.lng }),
      );

      map.current = instance;
      setMapReady(true);
    });

    return () => {
      abandoned = true;
      instance?.remove();
      map.current = null;
      setMapReady(false);
    };
  }, []);

  useEffect(() => {
    void reload().catch(() => setError("Lecture impossible."));
  }, [reload]);

  // Report des points sur la carte.
  useEffect(() => {
    const instance = map.current;
    if (!instance) return;

    for (const marker of markers.current) marker.remove();
    markers.current = [];

    for (const poi of pois) {
      const dot = document.createElement("button");
      dot.type = "button";
      // min-height: buttons get 40 px from the stylesheet, which stretched the dots.
      dot.style.cssText = `width:18px;height:18px;min-height:0;border-radius:50%;border:2px solid #fff;
        background:${POI_COLORS[poi.category] ?? "#6c757d"};cursor:pointer;padding:0`;
      dot.title = poi.name["fr"] ?? poi.category;
      // Le libellé rend le point atteignable au clavier et au lecteur d'écran :
      // une pastille colorée seule ne dit rien.
      dot.setAttribute("aria-label", `${dot.title} — supprimer`);
      dot.onclick = async (event) => {
        event.stopPropagation();
        if (!confirm(`Supprimer « ${dot.title} » ?`)) return;
        await festivals
          .deletePoi(slug, poi.id)
          .catch(() => setError("Suppression impossible."));
        await reload();
      };

      markers.current.push(
        new Marker({ element: dot })
          .setLngLat([poi.longitude, poi.latitude])
          .addTo(instance),
      );
    }

    // Cadrage sur les points existants, plutôt qu'un centre arbitraire.
    if (pois.length > 0) {
      const bounds = new LngLatBounds();
      for (const poi of pois) bounds.extend([poi.longitude, poi.latitude]);
      instance.fitBounds(bounds, { padding: 60, maxZoom: 17, duration: 0 });
    }
  }, [pois, slug, reload, mapReady]);

  const create = async () => {
    if (!pending || !name.trim()) return;
    setError(null);
    try {
      await festivals.createPoi(slug, {
        category,
        latitude: pending.lat,
        longitude: pending.lng,
        translations: { fr: { name: name.trim() } },
      });
      setPending(null);
      setName("");
      await reload();
    } catch {
      setError("Création impossible.");
    }
  };

  return (
    <>
      <section className="card">
        <h2>Lieux</h2>
        <p className="muted">
          Cliquez sur la carte à l'endroit du lieu, nommez-le, ajoutez-le. Cliquez sur
          un point existant pour le retirer.
        </p>

        {error ? <p className="error">{error}</p> : null}

        <div className="row">
          <label style={{ minWidth: 200 }}>
            Catégorie
            <select
              value={category}
              onChange={(e) => setCategory(e.target.value as PoiCategory)}
            >
              {POI_CATEGORIES.map((value) => (
                <option key={value} value={value}>
                  {translate("fr", `poi.category.${value}` as never)}
                </option>
              ))}
            </select>
          </label>

          <label style={{ flex: 1, minWidth: 220 }}>
            Nom
            <input
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Toilettes nord"
            />
          </label>

          <button
            className="primary"
            onClick={() => void create()}
            disabled={!pending || !name.trim()}
          >
            Ajouter ici
          </button>
        </div>

        <p className="muted">
          {pending
            ? `Point choisi : ${pending.lat.toFixed(5)}, ${pending.lng.toFixed(5)}`
            : "Aucun point choisi — cliquez sur la carte."}
        </p>

        <div className="map" ref={container} />
      </section>

      <section className="card">
        <h2>{pois.length > 1 ? `${pois.length} lieux` : `${pois.length} lieu`}</h2>
        <div className="table-scroll">
          <table>
            <thead>
              <tr>
                <th>Nom</th>
                <th>Catégorie</th>
                <th>Vérifié</th>
              </tr>
            </thead>
            <tbody>
              {pois.map((poi) => (
                <tr key={poi.id}>
                  <td>{poi.name["fr"] ?? "—"}</td>
                  <td>{translate("fr", `poi.category.${poi.category}` as never)}</td>
                  <td>{poi.verified ? "oui" : "non"}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>
    </>
  );
}
